import type { ColumnType } from 'antd/es/table';

// Client-side sort + filter helpers for antd tables over a fully loaded list
// (e.g. the payroll detail drawer). antd hands `summary` the filtered rows, so a
// totals row built from its argument follows the filters on its own.

type Cell = string | number | null | undefined;

export const textSorter =
  <T>(pick: (r: T) => Cell) =>
  (a: T, b: T) =>
    String(pick(a) ?? '').localeCompare(String(pick(b) ?? ''), 'he');

export const numSorter =
  <T>(pick: (r: T) => number | null | undefined) =>
  (a: T, b: T) =>
    (pick(a) ?? 0) - (pick(b) ?? 0);

/** A checkbox filter over the values that actually appear in `rows`. Nothing
 *  when there's only one value — a filter with one option filters nothing and
 *  only takes header room. Search box once the list gets long. */
export function valueFilter<T>(
  rows: T[],
  pick: (r: T) => Cell,
  label: (v: string | number) => string = (v) => (v === '' ? '—' : String(v)),
): Pick<ColumnType<T>, 'filters' | 'onFilter' | 'filterSearch'> {
  const values = [...new Set(rows.map((r) => pick(r) ?? ''))].sort((a, b) =>
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'he'),
  );
  if (values.length < 2) return {};
  return {
    filters: values.map((v) => ({ text: label(v), value: v })),
    filterSearch: values.length > 6,
    onFilter: (v, r) => (pick(r) ?? '') === v,
  };
}

/** Numeric `scroll.x` for a table whose columns all carry a width: lets a wide
 *  table scroll sideways instead of clipping its last column, and keeps a
 *  `sticky` header lined up with the body. */
export const columnsWidth = (columns: { width?: number | string }[]) =>
  columns.reduce((a, c) => a + (typeof c.width === 'number' ? c.width : 100), 0);
